<template>
  <div class="vtm-filterbar">
    <!-- 结构化筛选（服务端）：变更即查询，无需搜索按钮 -->
    <template v-for="field in primaryFields">
      <el-select
        v-if="field.type === 'select'"
        :key="field.key"
        v-model="value[field.key]"
        size="small"
        clearable
        filterable
        class="vtm-filterbar__field"
        :style="fieldStyle(field)"
        :no-data-text="$t('common.noData')"
        :placeholder="field.placeholder"
        @change="emitSearch"
      >
        <el-option v-for="opt in field.options" :key="opt.value" :label="opt.label" :value="opt.value"></el-option>
      </el-select>

      <el-date-picker
        v-else-if="field.type === 'daterange'"
        :key="field.key"
        v-model="dateRange"
        type="daterange"
        size="small"
        class="vtm-filterbar__field vtm-filterbar__date"
        :picker-options="pickerOptions"
        range-separator="→"
        :start-placeholder="$t('searchOrder.start')"
        :end-placeholder="$t('searchOrder.end')"
        @change="onDateChange(field)"
      ></el-date-picker>

      <el-input
        v-else-if="field.type === 'text'"
        :key="field.key"
        v-model="value[field.key]"
        size="small"
        clearable
        class="vtm-filterbar__field"
        :style="fieldStyle(field)"
        :prefix-icon="field.icon || 'el-icon-search'"
        :placeholder="field.placeholder"
        @input="onTextInput"
        @clear="emitSearch"
        @keyup.enter.native="emitSearch"
      ></el-input>
    </template>

    <!-- 次级筛选收进浮层，保持单行 -->
    <el-popover v-if="secondaryFields.length" placement="bottom-start" width="300" trigger="click">
      <div class="vtm-filterbar__more">
        <div v-for="field in secondaryFields" :key="field.key" class="vtm-filterbar__more-row">
          <span class="vtm-filterbar__more-label">{{ field.placeholder }}</span>
          <el-select
            v-if="field.type === 'select'"
            v-model="value[field.key]"
            size="small"
            clearable
            filterable
            style="width: 100%"
            :no-data-text="$t('common.noData')"
            :placeholder="field.placeholder"
            @change="emitSearch"
          >
            <el-option v-for="opt in field.options" :key="opt.value" :label="opt.label" :value="opt.value"></el-option>
          </el-select>
          <el-input
            v-else
            v-model="value[field.key]"
            size="small"
            clearable
            @input="onTextInput"
            @clear="emitSearch"
            @keyup.enter.native="emitSearch"
          ></el-input>
        </div>
      </div>
      <el-button slot="reference" size="small" icon="el-icon-set-up" class="vtm-filterbar__more-btn">
        {{ $t("common.moreFilters") }}
        <span v-if="activeSecondaryCount" class="vtm-filterbar__badge">{{ activeSecondaryCount }}</span>
      </el-button>
    </el-popover>

    <el-button
      v-if="hasActiveFilters"
      type="text"
      size="small"
      class="vtm-filterbar__reset"
      icon="el-icon-refresh-left"
      @click="emitReset"
    >
      {{ $t("common.reset") }}
    </el-button>
  </div>
</template>

<script>
  // 内联式结构化筛选，供 VtmTable 的 #toolbar-filters 使用。
  // 无搜索按钮：下拉/日期变更即触发，文本输入防抖后触发。
  // field: { key, type:'select'|'daterange'|'text', placeholder, options, width, primary, startKey, endKey, icon }
  // primary===false 的字段收进「更多筛选」浮层。
  export default {
    name: "FilterBar",
    props: {
      value: { type: Object, required: true },
      fields: { type: Array, required: true },
      debounce: { type: Number, default: 350 }
    },
    data() {
      return {
        dateRange: [],
        debounceTimer: null,
        pickerOptions: {
          shortcuts: [
            {
              text: this.$t("txReview.today"),
              onClick(picker) {
                const end = new Date();
                const start = new Date();
                start.setHours(0, 0, 0, 0);
                picker.$emit("pick", [start, end]);
              }
            },
            {
              text: this.$t("txReview.days7"),
              onClick(picker) {
                const end = new Date();
                const start = new Date();
                start.setTime(start.getTime() - 3600 * 1000 * 24 * 7);
                picker.$emit("pick", [start, end]);
              }
            },
            {
              text: this.$t("txReview.days30"),
              onClick(picker) {
                const end = new Date();
                const start = new Date();
                start.setTime(start.getTime() - 3600 * 1000 * 24 * 30);
                picker.$emit("pick", [start, end]);
              }
            }
          ]
        }
      };
    },
    computed: {
      primaryFields() {
        return this.fields.filter(field => field.primary !== false || field.type === "daterange");
      },
      secondaryFields() {
        return this.fields.filter(field => field.primary === false && field.type !== "daterange");
      },
      activeSecondaryCount() {
        return this.secondaryFields.filter(field => {
          const v = this.value[field.key];
          return v !== undefined && v !== null && v !== "";
        }).length;
      },
      hasActiveFilters() {
        return this.fields.some(field => {
          if (field.type === "daterange") {
            return this.dateRange && this.dateRange.length > 0;
          }
          const v = this.value[field.key];
          return v !== undefined && v !== null && v !== "";
        });
      }
    },
    methods: {
      fieldStyle(field) {
        return field.width ? { width: field.width } : {};
      },
      onTextInput() {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => this.emitSearch(), this.debounce);
      },
      onDateChange(field) {
        const startKey = field.startKey || "startTime";
        const endKey = field.endKey || "endTime";
        if (this.dateRange && this.dateRange.length === 2) {
          const start = new Date(this.dateRange[0]);
          const end = new Date(this.dateRange[1]);
          end.setHours(23, 59, 59, 999);
          this.value[startKey] = this.$timeUtil.formatTime(start);
          this.value[endKey] = this.$timeUtil.formatTime(end);
        } else {
          this.value[startKey] = "";
          this.value[endKey] = "";
        }
        this.emitSearch();
      },
      emitSearch() {
        clearTimeout(this.debounceTimer);
        this.$emit("search", this.value);
      },
      emitReset() {
        clearTimeout(this.debounceTimer);
        this.dateRange = [];
        this.fields.forEach(field => {
          if (field.type === "daterange") {
            this.value[field.startKey || "startTime"] = "";
            this.value[field.endKey || "endTime"] = "";
          } else {
            this.value[field.key] = "";
          }
        });
        this.$emit("reset", this.value);
      }
    },
    beforeDestroy() {
      clearTimeout(this.debounceTimer);
    }
  };
</script>

<style scoped lang="scss">
  .vtm-filterbar {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
  }

  .vtm-filterbar__field {
    width: 160px;
  }

  .vtm-filterbar__date {
    width: 240px;
  }

  .vtm-filterbar__more {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .vtm-filterbar__more-row {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .vtm-filterbar__more-label {
    font-size: 12px;
    font-weight: 600;
    color: var(--vtm-ink-2);
  }

  .vtm-filterbar__badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 16px;
    height: 16px;
    padding: 0 4px;
    margin-left: 4px;
    border-radius: 8px;
    background: var(--vtm-mint);
    color: var(--vtm-navy);
    font-size: 10.5px;
    font-weight: 700;
    line-height: 1;
  }
</style>
